import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'

import { GROUPS, entryAddress, entryScreen, findEntries, type Entry } from './entries'
import { fromIndex } from './SettingsFrame'

// The Settings index: where #/settings lands (release 3.3, plan 3.9 of the
// 7 Oct UX plan).
//
// # WHY AN INDEX AGAIN
//
// The card grid this replaced (eighteen cards, Settings.tsx) was taken out
// because it treated every setting as equally important and said nothing of
// what each one decides. Its replacement opened the first entry held, so
// somebody who came to change one setting landed on Organisation and searched
// the bar's five groups for it, and the way back from an entry led to
// Organisation, not to the list of settings. Plan 3.9 is the middle: every
// setting on one screen, one line each (its name and at most eight words on
// what it decides), under the five groups the bar uses, and a search that finds
// a setting by its name or by what it does. It fits one window at 1440 x 900.
//
// # THE SEARCH
//
// A combobox: type, and the settings that match are listed under it with the
// first one chosen; Up and Down move the choice, Enter opens it, Escape clears
// the search. A person who never touches it has the index, every entry a plain
// link at its own address.
//
// # FOCUS
//
// Back to the index from an entry's breadcrumb takes the link that was pressed
// off the page with the entry. The focus would fall to the page's body, so it
// is put on the index's heading instead, unless something already holds it.
// Opening an entry from here does the same the other way: the frame puts the
// focus on the entry's heading (fromIndex, SettingsFrame.tsx).

export function SettingsIndex({ shown }: { shown: Entry[] }) {
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const id = useId()
  const heading = useRef<HTMLHeadingElement>(null)
  const found = useMemo(() => findEntries(query, shown), [query, shown])
  const searching = query.trim() !== ''
  const chosen = found[Math.min(active, found.length - 1)]

  // Up and Down move the choice while the focus stays in the search box: the
  // chosen row is scrolled into view so a long list never hides it.
  useEffect(() => {
    if (!searching || !chosen) return
    document.getElementById(id + 'o-' + chosen.key)?.scrollIntoView?.({ block: 'nearest' })
  }, [searching, chosen, id])

  useEffect(() => {
    const a = document.activeElement
    if (a && a !== document.body) return
    heading.current?.focus({ preventScroll: true })
  }, [])

  const open = (e: Entry) => {
    fromIndex.current = true
    window.location.hash = entryAddress(e).replace(/^#/, '')
  }
  const onKey = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'ArrowDown' && found.length > 0) {
      ev.preventDefault()
      setActive((a) => Math.min(a + 1, found.length - 1))
    } else if (ev.key === 'ArrowUp' && found.length > 0) {
      ev.preventDefault()
      setActive((a) => Math.max(a - 1, 0))
    } else if (ev.key === 'Enter' && chosen) {
      ev.preventDefault()
      open(chosen)
    } else if (ev.key === 'Escape' && query) {
      ev.preventDefault()
      setQuery('')
      setActive(0)
    }
  }

  const groups = GROUPS.filter((g) => shown.some((e) => e.group === g))
  const groupId = (g: string) => id + 'g-' + g.toLowerCase().replace(/\W+/g, '-')

  return (
    <div className="p1-page settings-index">
      <div className="settings-index-head">
        <h1 ref={heading} tabIndex={-1}>
          Settings
        </h1>
        <input
          type="search"
          className="settings-search"
          role="combobox"
          aria-label="Find a setting"
          placeholder="Find a setting"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={searching && found.length > 0}
          aria-controls={searching && found.length > 0 ? id + 'found' : undefined}
          aria-activedescendant={searching && chosen ? id + 'o-' + chosen.key : undefined}
          value={query}
          onChange={(ev) => {
            setQuery(ev.target.value)
            setActive(0)
          }}
          onKeyDown={onKey}
        />
      </div>
      {/* How many the search found, said to a screen reader as it changes. */}
      <p className="vh" role="status">
        {searching ? (found.length === 1 ? '1 setting found' : found.length + ' settings found') : ''}
      </p>
      {searching ? (
        found.length === 0 ? (
          <p className="muted settings-none">No setting matches that. Try a word from what it decides, such as approve or mail.</p>
        ) : (
          <ul className="settings-found" role="listbox" id={id + 'found'} aria-label="Settings found">
            {found.map((e, i) => (
              <li
                key={e.key}
                id={id + 'o-' + e.key}
                role="option"
                aria-selected={e === chosen}
                // Pressed with the pointer: the input keeps the focus (mouse
                // down does nothing else), and the entry opens on a click of the
                // main button, which is also what a screen reader's activation
                // sends; another button or a modified click does nothing.
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={(ev) => {
                  if (ev.button !== 0 || ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey) return
                  open(e)
                }}
                onMouseEnter={() => setActive(i)}
              >
                <b>{e.label}</b>
                <span className="settings-about">{e.about}</span>
                <span className="settings-in">{e.group}</span>
              </li>
            ))}
          </ul>
        )
      ) : (
        <div
          className="settings-groups"
          onClick={(ev) => {
            if ((ev.target as Element).closest('a')) fromIndex.current = true
          }}
        >
          {groups.map((g) => {
            const entries = shown.filter((e) => e.group === g)
            return (
              <section key={g} className="settings-group" aria-labelledby={groupId(g)}>
                <h2 id={groupId(g)}>
                  {g} <span className="settings-count">{entries.length}</span>
                </h2>
                <ul role="list">
                  {entries.map((e) => (
                    <li key={e.key}>
                      <a href={entryAddress(e)} data-nav-id={'tprm.' + entryScreen(e)} className="settings-line">
                        <b>{e.label}</b>
                        <span className="settings-about">{e.about}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}
